import React, { useState, useEffect, useRef } from 'react';
import {Link, useNavigate} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {Shield, ChevronDown, ChevronUp, MapPin, ExternalLink, CheckCircle, Home} from 'lucide-react';
import { authService } from '../../services/authService';
import { useLanguage } from '../../hooks/useLanguage';
import { getCountryFromLanguage } from '../../types/i18n';
import { AddressAutocomplete } from './AddressAutocomplete';
import { AddressSuggestion } from '../../services/addressService';
import {
    GarageRegistration,
    Speciality,
    User,
    VehicleType,
    WeekDay,
    BusinessHour,
    BusinessHours
} from '../../types/auth';
import icon from '../../img/icon.png';
import { useLocation } from 'react-router-dom';
import { GarageImageUpload } from '../GarageImageUpload';

const DESCRIPTION_MAX_LENGTH = 1000;

export const RegisterForm: React.FC = () => {
    const { t } = useTranslation();
    const { isRTL, currentLanguage } = useLanguage();
    const navigate = useNavigate();
    const location = useLocation();

    // Configuration selon la langue actuelle
    const currentCountry = getCountryFromLanguage(currentLanguage);

    // États du composant
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [currentStep, setCurrentStep] = useState(1);
    const [showSchedule, setShowSchedule] = useState(false);
    const [locationLoading, setLocationLoading] = useState(false);
    const [locationError, setLocationError] = useState('');
    const [acceptedTerms, setAcceptedTerms] = useState<boolean>(false);
    // Précision de la dernière adresse choisie dans les suggestions (null = saisie manuelle)
    const [pickedPrecision, setPickedPrecision] = useState<AddressSuggestion['precision'] | null>(null);

    // États pour les photos (section Informations)
    const [logo, setLogo] = useState<File | null>(null);
    const [garagePhotos, setGaragePhotos] = useState<File[]>([]);

    // Refs pour les sections
    const personalInfoRef = useRef<HTMLDivElement>(null);
    const addressInfoRef = useRef<HTMLDivElement>(null);

    // États pour la validation
    const [fieldErrors, setFieldErrors] = useState<{[key: string]: string}>({});
    const [descriptionLength, setDescriptionLength] = useState(0);

    const defaultBusinessHour: BusinessHour = {
        open: '09:00',
        close: '18:00',
        closed: false
    };

    // Fonctions de validation adaptées aux pays
    const validatePhoneNumber = (phone: string): boolean => {
        if (currentCountry === 'SA') {
            // Regex pour numéros saoudiens (+966 ou 05XXXXXXXX)
            const saudiPhoneRegex = /^(\+966|966|05)\d{8}$/;
            return saudiPhoneRegex.test(phone.replace(/\s/g, ''));
        } else {
            // Regex pour numéros français
            const frenchPhoneRegex = /^(?:(?:\+|00)33|0)\s*[1-9](?:[\s.-]*\d{2}){4}$/;
            return frenchPhoneRegex.test(phone);
        }
    };

    const validateZipCode = (zipCode: string): boolean => {
        if (currentCountry === 'SA') {
            // Code postal saoudien (5 chiffres)
            const saudiZipRegex = /^\d{5}$/;
            return saudiZipRegex.test(zipCode);
        } else {
            // Code postal français
            const frenchZipCodeRegex = /^(?:[0-8]\d|9[0-8])\d{3}$/;
            return frenchZipCodeRegex.test(zipCode);
        }
    };

    const validateEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    // Options de spécialités traduites
    const getSpecialityOptions = (): { value: Speciality; label: string }[] => [
        { value: 'MECANIQUE_GENERALE', label: t('register.specialties.options.MECANIQUE_GENERALE') },
        { value: 'CARROSSERIE', label: t('register.specialties.options.CARROSSERIE') },
        { value: 'CLIMATISATION', label: t('register.specialties.options.CLIMATISATION') },
        { value: 'DIAGNOSTIC_ELECTRONIQUE', label: t('register.specialties.options.DIAGNOSTIC_ELECTRONIQUE') },
        { value: 'PNEUMATIQUES', label: t('register.specialties.options.PNEUMATIQUES') },
        { value: 'VIDANGE', label: t('register.specialties.options.VIDANGE') },
        { value: 'FREINAGE', label: t('register.specialties.options.FREINAGE') },
        { value: 'SUSPENSION', label: t('register.specialties.options.SUSPENSION') },
        { value: 'ELECTRICITE', label: t('register.specialties.options.ELECTRICITE') },
        { value: 'HYBRIDE_ELECTRIQUE', label: t('register.specialties.options.HYBRIDE_ELECTRIQUE') }
    ];

    // Options de types de véhicules traduites
    const getVehicleTypeOptions = (): { value: VehicleType; label: string }[] => [
        { value: 'ALL_VEHICLE', label: t('register.vehicleTypes.options.ALL_VEHICLE') },
        { value: 'TOYOTA', label: t('register.vehicleTypes.options.TOYOTA') },
        { value: 'HYUNDAI', label: t('register.vehicleTypes.options.HYUNDAI') },
        { value: 'FORD', label: t('register.vehicleTypes.options.FORD') },
        { value: 'MAZDA', label: t('register.vehicleTypes.options.MAZDA') },
        { value: 'KOREAN', label: t('register.vehicleTypes.options.KOREAN') },
        { value: 'AMERICAN', label: t('register.vehicleTypes.options.AMERICAN') },
        { value: 'CHINESE', label: t('register.vehicleTypes.options.CHINESE') },
        { value: 'CHANGAN', label: t('register.vehicleTypes.options.CHANGAN') },
        { value: 'GERMAN', label: t('register.vehicleTypes.options.GERMAN') }
    ];

    // Jours de la semaine traduits
    const getWeekdays = (): { id: WeekDay; label: string }[] => [
        { id: 'MONDAY', label: t('register.businessHours.days.MONDAY') },
        { id: 'TUESDAY', label: t('register.businessHours.days.TUESDAY') },
        { id: 'WEDNESDAY', label: t('register.businessHours.days.WEDNESDAY') },
        { id: 'THURSDAY', label: t('register.businessHours.days.THURSDAY') },
        { id: 'FRIDAY', label: t('register.businessHours.days.FRIDAY') },
        { id: 'SATURDAY', label: t('register.businessHours.days.SATURDAY') },
        { id: 'SUNDAY', label: t('register.businessHours.days.SUNDAY') }
    ];

    // Configuration de l'état du formulaire avec valeurs par défaut selon le pays
    const [formData, setFormData] = useState<GarageRegistration>({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        phoneNumber: '',
        garageName: '',
        siretNumber: '',
        isActive: false,
        classified: false,
        address: {
            street: '',
            zipCode: '',
            city: '',
            country: '',
            latitude: null,
            longitude: null,
            placeId: ''
        },
        garagePhone: '',
        website: '',
        description: '',
        specialities: [],
        vehicleTypes: [],
        businessHours: {
            MONDAY: { open: '09:00', close: '18:00', closed: false },
            TUESDAY: { open: '09:00', close: '18:00', closed: false },
            WEDNESDAY: { open: '09:00', close: '18:00', closed: false },
            THURSDAY: { open: '09:00', close: '18:00', closed: false },
            FRIDAY: { open: '09:00', close: '18:00', closed: false }, // Vendredi fermé en Arabie Saoudite
            SATURDAY: { open: '09:00', close: '18:00', closed: true }, // Samedi ouvert en Arabie Saoudite
            SUNDAY: { open: '09:00', close: '18:00', closed: true }
        },
        subscription: "COMMISSION" // Nouveau modèle de commission
    });

    // Fonction utilitaire pour accéder aux horaires d'ouverture
    const getBusinessHour = (dayId: WeekDay): BusinessHour => {
        return formData.businessHours[dayId as keyof BusinessHours];
    };

    // Fonctions de validation des étapes
    const validateStep1 = (): boolean => {
        const errors: {[key: string]: string} = {};

        if (!formData.firstName.trim()) {
            errors.firstName = t('register.validation.firstNameRequired');
        }

        if (!formData.lastName.trim()) {
            errors.lastName = t('register.validation.lastNameRequired');
        }

        if (!formData.email.trim()) {
            errors.email = t('register.validation.emailRequired');
        } else if (!validateEmail(formData.email)) {
            errors.email = t('register.validation.emailInvalid');
        }

        if (!formData.password.trim()) {
            errors.password = t('register.validation.passwordRequired');
        } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&.])[A-Za-z\d@$!%*?&.]{8,}$/.test(formData.password)) {
            errors.password = t('register.validation.passwordComplexity');
        }

        if (!formData.phoneNumber.trim()) {
            errors.phoneNumber = t('register.validation.phoneRequired');
        }

        if (!formData.garageName.trim()) {
            errors.garageName = t('register.validation.garageNameRequired');
        }

        if (!formData.garagePhone.trim()) {
            errors.garagePhone = t('register.validation.garagePhoneRequired');
        }

        if (!formData.description || !formData.description.trim()) {
            errors.description = t('register.validation.descriptionRequired');
        } else if (formData.description.length > DESCRIPTION_MAX_LENGTH) {
            errors.description = t('register.validation.descriptionMaxLength', { max: DESCRIPTION_MAX_LENGTH });
        }

        if (!logo) {
            errors.logo = t('register.validation.logoRequired');
        }

        if (garagePhotos.length === 0) {
            errors.garagePhotos = t('register.validation.garagePhotosRequired');
        }

        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const validateStep2 = (): boolean => {
        const errors: {[key: string]: string} = {};

        if (!formData.address.street.trim()) {
            errors['address.street'] = t('register.validation.streetRequired');
        }

        if (!formData.address.zipCode.trim()) {
            errors['address.zipCode'] = t('register.validation.zipCodeRequired');
        }

        if (!formData.address.city.trim()) {
            errors['address.city'] = t('register.validation.cityRequired');
        }

        if (!formData.address.latitude) {
            errors['address.coordinates'] = t('register.validation.coordinatesRequired');
        }

        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const validateStep3 = (): boolean => {
        const errors: {[key: string]: string} = {};

        for (const day of getWeekdays()) {
            const businessHour = getBusinessHour(day.id);
            if (!businessHour.closed) {
                if (!businessHour.open) {
                    errors[`businessHours.${day.id}.open`] = t('register.validation.openingTimeRequired', { day: day.label });
                }
                if (!businessHour.close) {
                    errors[`businessHours.${day.id}.close`] = t('register.validation.closingTimeRequired', { day: day.label });
                }
            }
        }

        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const validateStep4 = (): boolean => {
        const errors: {[key: string]: string} = {};

        if (formData.specialities.length === 0) {
            errors.specialities = t('register.validation.specialtiesRequired');
        }

        if (formData.vehicleTypes.length === 0) {
            errors.vehicleTypes = t('register.validation.vehicleTypesRequired');
        }

        if (!acceptedTerms) {
            errors.terms = t('register.validation.termsRequired');
        }

        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Remplit rue, code postal, ville et coordonnées GPS depuis la suggestion choisie
    const handleAddressSelect = (suggestion: AddressSuggestion) => {
        setFormData(prev => ({
            ...prev,
            address: {
                ...prev.address,
                street: suggestion.street,
                zipCode: suggestion.zipCode,
                city: suggestion.city,
                latitude: suggestion.latitude,
                longitude: suggestion.longitude
            }
        }));
        setPickedPrecision(suggestion.precision);
        setFieldErrors(prev => {
            const next = { ...prev };
            ['address.street', 'address.zipCode', 'address.city', 'address.coordinates'].forEach(key => delete next[key]);
            return next;
        });
    };

    // Modifier à la main une adresse choisie dans les suggestions rend ses coordonnées caduques
    const updateAddressField = (field: 'street' | 'zipCode' | 'city', value: string) => {
        setFormData(prev => ({
            ...prev,
            address: {
                ...prev.address,
                [field]: value,
                ...(pickedPrecision ? { latitude: null, longitude: null } : {})
            }
        }));
        setPickedPrecision(null);
    };

    // Fonction de géolocalisation
    const getCurrentLocation = () => {
        setLocationLoading(true);
        setLocationError('');

        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { latitude, longitude } = position.coords;
                    setFormData({
                        ...formData,
                        address: {
                            ...formData.address,
                            latitude,
                            longitude
                        }
                    });
                    setLocationLoading(false);
                },
                (error) => {
                    let errorMessage = t('register.errors.unableToGetLocation');
                    switch (error.code) {
                        case error.PERMISSION_DENIED:
                            errorMessage = t('register.errors.locationDenied');
                            break;
                        case error.POSITION_UNAVAILABLE:
                            errorMessage = t('register.errors.locationUnavailable');
                            break;
                        case error.TIMEOUT:
                            errorMessage = t('register.errors.locationTimeout');
                            break;
                    }
                    setLocationError(errorMessage);
                    setLocationLoading(false);
                },
                {
                    enableHighAccuracy: true,
                    timeout: 10000
                }
            );
        } else {
            setLocationError(t('register.errors.geolocationNotSupported'));
            setLocationLoading(false);
        }
    };

    // Navigation entre les étapes
    const nextStep = () => {
        let isValid = false;

        switch (currentStep) {
            case 1:
                isValid = validateStep1();
                break;
            case 2:
                isValid = validateStep2();
                break;
            case 3:
                isValid = validateStep3();
                break;
            case 4:
                isValid = validateStep4();
                break;
            default:
                isValid = true;
        }

        if (isValid) {
            setCurrentStep(currentStep + 1);
            setFieldErrors({});
            window.scrollTo({ top: 0, behavior: 'smooth' });

            if (currentStep === 1) {
                setTimeout(() => {
                    if (addressInfoRef.current) {
                        addressInfoRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                }, 100);
            }
        }
    };

    const prevStep = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
            setFieldErrors({});
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
            navigate('/login');
        }
    };

    // Gestion des sélections multiples
    const handleAllSpecialities = (checked: boolean) => {
        if (checked) {
            const allValues = getSpecialityOptions().map(option => option.value);
            setFormData({ ...formData, specialities: allValues });
        } else {
            setFormData({ ...formData, specialities: [] });
        }
    };

    const handleAllVehicleTypes = (checked: boolean) => {
        if (checked) {
            const allValues = getVehicleTypeOptions().map(option => option.value);
            setFormData({ ...formData, vehicleTypes: allValues });
        } else {
            setFormData({ ...formData, vehicleTypes: [] });
        }
    };

    // Gestion des horaires d'ouverture
    const handleBusinessHoursChange = (day: WeekDay, field: keyof BusinessHour, value: string | boolean) => {
        setFormData({
            ...formData,
            businessHours: {
                ...formData.businessHours,
                [day]: {
                    ...getBusinessHour(day),
                    [field]: value
                }
            }
        });
    };

    // Gestion de la description
    const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        if (value.length <= DESCRIPTION_MAX_LENGTH) {
            setFormData({ ...formData, description: value });
        }
        setDescriptionLength(value.length);
    };

    // Soumission du formulaire - DIRECTEMENT VERS LA BASE DE DONNÉES
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateStep4()) {
            return;
        }

        try {
            setLoading(true);
            setError('');

            const dataToSubmit = {
                ...formData,
                acceptedTerms,
                country: currentCountry,
                preferredLanguage: currentLanguage,
                logo,
                garagePhotos
            };

            // Logo + photos envoyés en multipart dans la même requête
            await authService.registerWithTapPayment(dataToSubmit);

            setTimeout(() => {
                navigate('/registration-success', { replace: true });
            }, 100);

        } catch (err: any) {
            console.error("Registration error:", err);
            if (err.response?.status === 429) {
                setError(t('register.errors.rateLimited'));
            } else {
                setError(err.message || t('register.errors.generalError'));
            }
        } finally {
            setLoading(false);
        }
    };

    // Classes CSS
    const inputClassName = `mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${isRTL ? 'text-right' : 'text-left'}`;
    const sectionClassName = "bg-white rounded-lg shadow-sm p-6 border border-gray-100";
    const labelClassName = `block text-sm font-medium text-gray-700 mb-1 ${isRTL ? 'text-right' : 'text-left'}`;

    const getInputClassName = (fieldName: string) => {
        return `${inputClassName} ${fieldErrors[fieldName] ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`;
    };

    // Composant d'erreur de champ
    const FieldError = ({ name }: { name: string }) => {
        if (!fieldErrors[name]) return null;

        return (
            <p className={`mt-1 text-sm text-red-600 ${isRTL ? 'text-right' : 'text-left'}`}>
                {fieldErrors[name]}
            </p>
        );
    };

    // Indicateur de progression
    const ProgressIndicator: React.FC = () => (
        <div className="mb-8">
            <div className="flex justify-between">
                {[1, 2, 3, 4].map((step) => (
                    <div
                        key={step}
                        className={`flex flex-col items-center ${currentStep >= step ? 'text-blue-600' : 'text-gray-400'}`}
                    >
                        <div
                            className={`flex items-center justify-center w-8 h-8 rounded-full font-medium mb-2
                ${currentStep > step
                                ? 'bg-blue-100 text-blue-600 border-2 border-blue-600'
                                : currentStep === step
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-200 text-gray-500'}`}
                        >
                            {step}
                        </div>
                        <span className={`text-xs font-medium hidden sm:block ${isRTL ? 'text-right' : 'text-left'}`}>
              {step === 1 && t('register.steps.information')}
                            {step === 2 && t('register.steps.address')}
                            {step === 3 && t('register.steps.schedule')}
                            {step === 4 && t('register.steps.specialties')}
            </span>
                    </div>
                ))}
            </div>
            <div className="mt-2 h-1 bg-gray-200 rounded-full">
                <div
                    className="h-1 bg-blue-600 rounded-full transition-all duration-300"
                    style={{ width: `${(currentStep - 1) * 33.33}%` }}
                ></div>
            </div>
        </div>
    );

    // Section gratuite - NOUVEAU
    const renderFreeOfferSection = () => (
        <div className={sectionClassName}>

            <div className="bg-green-50 rounded-lg p-6 border-2 border-green-200">
                <div className={`flex items-center justify-center mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <CheckCircle className="h-8 w-8 text-green-600 mr-3" />
                    <h4 className={`text-2xl font-bold text-green-800 ${isRTL ? 'text-right' : 'text-left'}`}>
                        {t('register.freeOffer.title')}
                    </h4>
                </div>

                {/*<div className="text-center mb-4">
                    <p className={`text-lg text-green-700 font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
                        {t('register.freeOffer.description')}
                    </p>
                </div>*/}

               {/* <div className="bg-white rounded-lg p-4 border border-green-200">
                    <h5 className={`font-semibold text-green-800 mb-3 ${isRTL ? 'text-right' : 'text-left'}`}>
                        {t('register.freeOffer.includes')}
                    </h5>
                    <ul className={`space-y-2 text-sm text-green-700 ${isRTL ? 'text-right' : 'text-left'}`}>
                        <li className={`flex items-center ${isRTL ? 'justify-end' : 'justify-start'}`}>
                            <CheckCircle className={`h-4 w-4 text-green-600 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                            {t('register.freeOffer.features.noSetupFee')}
                        </li>
                        <li className={`flex items-center ${isRTL ? 'justify-end' : 'justify-start'}`}>
                            <CheckCircle className={`h-4 w-4 text-green-600 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                            {t('register.freeOffer.features.commissionBased')}
                        </li>
                        <li className={`flex items-center ${isRTL ? 'justify-end' : 'justify-start'}`}>
                            <CheckCircle className={`h-4 w-4 text-green-600 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                            {t('register.freeOffer.features.unlimitedInspections')}
                        </li>
                        <li className={`flex items-center ${isRTL ? 'justify-end' : 'justify-start'}`}>
                            <CheckCircle className={`h-4 w-4 text-green-600 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                            {t('register.freeOffer.features.aiDiagnostics')}
                        </li>
                        <li className={`flex items-center ${isRTL ? 'justify-end' : 'justify-start'}`}>
                            <CheckCircle className={`h-4 w-4 text-green-600 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                            {t('register.freeOffer.features.fullSupport')}
                        </li>
                    </ul>
                </div>*/}
            </div>
        </div>
    );

    // Termes et conditions
    const renderTermsAndConditions = () => (
        <div className="mt-6 mb-4">
            <div className={`flex items-start ${isRTL ? 'justify-start' : 'justify-start'}`}>
                <div className="flex items-center h-5">
                    <input
                        id="terms"
                        name="terms"
                        type="checkbox"
                        required
                        checked={acceptedTerms}
                        onChange={(e) => setAcceptedTerms(e.target.checked)}
                        className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
                    />
                </div>
                <div className={`${isRTL ? 'mr-3' : 'ml-3'} text-sm`}>
                    <label htmlFor="terms" className={`font-medium text-gray-700 ${isRTL ? 'text-right' : 'text-left'}`}>
                        {t('register.terms.accept')}{' '}
                        <a
                            href="/cgu"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 underline inline-flex items-center"
                        >
                            {t('register.terms.termsOfService')}
                            <ExternalLink className={`w-3 h-3 ${isRTL ? 'mr-1' : 'ml-1'}`} />
                        </a>
                    </label>
                    <p className={`text-gray-500 ${isRTL ? 'text-right' : 'text-left'}`}>
                        {t('register.terms.registrationAgreement')}
                    </p>
                    {fieldErrors.terms && (
                        <p className={`mt-1 text-sm text-red-600 ${isRTL ? 'text-right' : 'text-left'}`}>
                            {fieldErrors.terms}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );

    // Section d'informations personnelles
    const renderPersonalInfo = () => (
        <div ref={personalInfoRef}>
            <div className={sectionClassName}>
                <h3 className={`text-lg font-medium text-gray-900 mb-4 ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('register.personalInfo.title')}
                </h3>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <div>
                        <label className={labelClassName}>{t('register.personalInfo.firstName')}</label>
                        <input
                            type="text"
                            required
                            className={getInputClassName('firstName')}
                            value={formData.firstName}
                            onChange={e => setFormData({ ...formData, firstName: e.target.value })}
                            dir={isRTL ? 'rtl' : 'ltr'}
                        />
                        <FieldError name="firstName" />
                    </div>

                    <div>
                        <label className={labelClassName}>{t('register.personalInfo.lastName')}</label>
                        <input
                            type="text"
                            required
                            className={getInputClassName('lastName')}
                            value={formData.lastName}
                            onChange={e => setFormData({ ...formData, lastName: e.target.value })}
                            dir={isRTL ? 'rtl' : 'ltr'}
                        />
                        <FieldError name="lastName" />
                    </div>

                    <div>
                        <label className={labelClassName}>{t('register.personalInfo.email')}</label>
                        <input
                            type="email"
                            required
                            className={getInputClassName('email')}
                            value={formData.email}
                            onChange={e => setFormData({ ...formData, email: e.target.value })}
                            dir="ltr"
                        />
                        <FieldError name="email" />
                    </div>

                    <div>
                        <label className={labelClassName}>{t('register.personalInfo.password')}</label>
                        <input
                            type="password"
                            required
                            className={getInputClassName('password')}
                            value={formData.password}
                            onChange={e => setFormData({ ...formData, password: e.target.value })}
                            dir="ltr"
                        />
                        <FieldError name="password" />
                    </div>

                    <div>
                        <label className={labelClassName}>{t('register.personalInfo.personalPhone')}</label>
                        <input
                            type="tel"
                            required
                            className={getInputClassName('phoneNumber')}
                            value={formData.phoneNumber}
                            onChange={e => setFormData({ ...formData, phoneNumber: e.target.value })}
                            dir="ltr"
                            placeholder=''
                        />
                        <FieldError name="phoneNumber" />
                    </div>
                </div>
            </div>

            {/* Informations du garage */}
            <div className={sectionClassName}>
                <h3 className={`text-lg font-medium text-gray-900 mb-4 ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('register.garageInfo.title')}
                </h3>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <div>
                        <label className={labelClassName}>{t('register.garageInfo.garageName')}</label>
                        <input
                            type="text"
                            required
                            className={getInputClassName('garageName')}
                            value={formData.garageName}
                            onChange={e => setFormData({ ...formData, garageName: e.target.value })}
                            dir={isRTL ? 'rtl' : 'ltr'}
                        />
                        <FieldError name="garageName" />
                    </div>

                    <div>
                     {/*   <label className={labelClassName}>{t('register.garageInfo.sirenNumber')}</label>
                        <input
                            type="text"
                            className={getInputClassName('siretNumber')}
                            value={formData.siretNumber}
                            onChange={e => setFormData({ ...formData, siretNumber: e.target.value })}
                            dir="ltr"
                        />*/}
                    </div>

                    <div>
                        <label className={labelClassName}>{t('register.garageInfo.garagePhone')}</label>
                        <input
                            type="tel"
                            required
                            className={getInputClassName('garagePhone')}
                            value={formData.garagePhone}
                            onChange={e => setFormData({ ...formData, garagePhone: e.target.value })}
                            dir="ltr"
                            placeholder=''
                        />
                        <FieldError name="garagePhone" />
                    </div>

                    <div>
                        <label className={labelClassName}>{t('register.garageInfo.website')}</label>
                        <input
                            type="url"
                            className={getInputClassName('website')}
                            value={formData.website}
                            onChange={e => setFormData({ ...formData, website: e.target.value })}
                            placeholder="https://"
                            dir="ltr"
                        />
                        <p className={`text-xs text-gray-500 mt-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                            {t('register.garageInfo.websiteOptional')}
                        </p>
                        <FieldError name="website" />
                    </div>

                    {/*<div className="mt-6 mb-4">
                        <div className={`flex items-start ${isRTL ? 'justify-start' : 'justify-start'}`}>
                            <div className="flex items-center h-5">
                                <input
                                    type="checkbox"
                                    required
                                    checked={formData.classified}
                                    onChange={e => setFormData({ ...formData, classified:  e.target.checked })}
                                    className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
                                />
                            </div>
                            <div className={`${isRTL ? 'mr-3' : 'ml-3'} text-sm`}>
                                <p className={`text-gray-500 ${isRTL ? 'text-right' : 'text-left'}`}>
                                    {t('register.classified')}
                                </p>
                            </div>
                        </div>
                    </div>*/}

                    <div className="sm:col-span-2">
                        <label className={labelClassName}>{t('register.garageInfo.description')}</label>
                        <textarea
                            required
                            className={`${getInputClassName('description')} min-h-[100px]`}
                            value={formData.description || ''}
                            onChange={handleDescriptionChange}
                            placeholder={t('register.garageInfo.descriptionPlaceholder')}
                            maxLength={DESCRIPTION_MAX_LENGTH}
                            dir={isRTL ? 'rtl' : 'ltr'}
                        />
                        <div className={`flex justify-between mt-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <p className={`text-xs text-gray-500 ${isRTL ? 'text-right' : 'text-left'}`}>
                                {t('register.garageInfo.descriptionHelper')}
                            </p>
                            <p className={`text-xs ${descriptionLength > DESCRIPTION_MAX_LENGTH * 0.9 ? 'text-orange-500' : 'text-gray-500'}`}>
                                {t('register.garageInfo.charactersCount', { current: descriptionLength, max: DESCRIPTION_MAX_LENGTH })}
                            </p>
                        </div>
                        <FieldError name="description" />
                    </div>

                    <div className="sm:col-span-2">
                        <GarageImageUpload
                            logo={logo}
                            garagePhotos={garagePhotos}
                            onLogoChange={setLogo}
                            onPhotosChange={setGaragePhotos}
                            logoError={fieldErrors.logo}
                            photosError={fieldErrors.garagePhotos}
                        />
                    </div>
                </div>
            </div>
        </div>
    );

    // Section d'adresse
    const renderAddressInfo = () => (
        <div ref={addressInfoRef}>
            <div className={sectionClassName}>
                <h3 className={`text-lg font-medium text-gray-900 mb-4 ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('register.addressInfo.title')}
                </h3>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                        <label className={labelClassName}>{t('register.addressInfo.street')}</label>
                        <AddressAutocomplete
                            value={formData.address.street}
                            onChange={street => updateAddressField('street', street)}
                            onSelect={handleAddressSelect}
                            inputClassName={getInputClassName('address.street')}
                            isRTL={isRTL}
                        />
                        <p className={`mt-1 text-xs text-gray-500 ${isRTL ? 'text-right' : 'text-left'}`}>
                            {t('register.addressInfo.searchHint')}
                        </p>
                        {pickedPrecision && (
                            <p className={`mt-1 text-xs ${pickedPrecision === 'housenumber' ? 'text-green-600' : 'text-amber-600'} ${isRTL ? 'text-right' : 'text-left'}`}>
                                {pickedPrecision === 'housenumber'
                                    ? t('register.addressInfo.positionFilled')
                                    : t('register.addressInfo.positionApproximate')}
                            </p>
                        )}
                        <FieldError name="address.street" />
                    </div>

                    <div>
                        <label className={labelClassName}>{t('register.addressInfo.zipCode')}</label>
                        <input
                            type="text"
                            required
                            className={getInputClassName('address.zipCode')}
                            value={formData.address.zipCode}
                            onChange={e => updateAddressField('zipCode', e.target.value)}
                            dir="ltr"
                        />
                        <FieldError name="address.zipCode" />
                    </div>

                    <div>
                        <label className={labelClassName}>{t('register.addressInfo.city')}</label>
                        <input
                            type="text"
                            required
                            className={getInputClassName('address.city')}
                            value={formData.address.city}
                            onChange={e => updateAddressField('city', e.target.value)}
                            dir={isRTL ? 'rtl' : 'ltr'}
                        />
                        <FieldError name="address.city" />
                    </div>
                </div>
            </div>

            {/* Localisation précise */}
            <div className={sectionClassName}>
                <div className={`flex justify-between items-center mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <h3 className={`text-lg font-medium text-gray-900 ${isRTL ? 'text-right' : 'text-left'}`}>
                        {t('register.location.title')}
                    </h3>
                    <button
                        type="button"
                        onClick={getCurrentLocation}
                        disabled={locationLoading}
                        className={`flex items-center text-sm font-medium text-blue-600 hover:text-blue-800 ${isRTL ? 'flex-row-reverse' : ''}`}
                    >
                        <MapPin className={`w-4 h-4 ${isRTL ? 'ml-1' : 'mr-1'}`} />
                        {locationLoading ? t('register.location.locating') : t('register.location.useCurrentLocation')}
                    </button>
                </div>

                <p className={`text-sm text-gray-600 mb-4 ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('register.location.description')}
                </p>

                {locationError && (
                    <div className="bg-red-50 text-red-600 p-3 rounded-md mb-4 text-sm">
                        <span className={isRTL ? 'text-right' : 'text-left'}>{locationError}</span>
                    </div>
                )}

                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 mb-4">
                    <div>
                        <label className={labelClassName}>{t('register.location.latitude')}</label>
                        <input
                            type="text"
                            required
                            className={getInputClassName('address.coordinates')}
                            value={formData.address.latitude || ''}
                            onChange={e => {
                                const lat = parseFloat(e.target.value);
                                if (!isNaN(lat)) {
                                    setFormData({
                                        ...formData,
                                        address: { ...formData.address, latitude: lat }
                                    });
                                }
                            }}
                            placeholder={t('register.location.latitudePlaceholder')}
                            dir="ltr"
                        />
                    </div>

                    <div>
                        <label className={labelClassName}>{t('register.location.longitude')}</label>
                        <input
                            type="text"
                            required
                            className={inputClassName}
                            value={formData.address.longitude || ''}
                            onChange={e => {
                                const lng = parseFloat(e.target.value);
                                if (!isNaN(lng)) {
                                    setFormData({
                                        ...formData,
                                        address: { ...formData.address, longitude: lng }
                                    });
                                }
                            }}
                            placeholder={t('register.location.longitudePlaceholder')}
                            dir="ltr"
                        />
                    </div>
                    {fieldErrors['address.coordinates'] && (
                        <div className="col-span-2">
                            <p className={`text-sm text-red-600 ${isRTL ? 'text-right' : 'text-left'}`}>
                                {fieldErrors['address.coordinates']}
                            </p>
                        </div>
                    )}
                </div>

                {/* Carte OpenStreetMap */}
                <div className="h-96 w-full rounded-lg overflow-hidden border border-gray-200 mb-4">
                    {formData.address.latitude && formData.address.longitude ? (
                        <div className="relative h-full">
                            <iframe
                                width="100%"
                                height="100%"
                                frameBorder="0"
                                scrolling="no"
                                marginHeight={0}
                                marginWidth={0}
                                src={`https://www.openstreetmap.org/export/embed.html?bbox=${formData.address.longitude-0.01},${formData.address.latitude-0.01},${formData.address.longitude+0.01},${formData.address.latitude+0.01}&layer=mapnik&marker=${formData.address.latitude},${formData.address.longitude}`}
                                style={{ border: 0 }}
                                title="Localisation du garage"
                            />
                            <div className={`absolute top-2 z-10 bg-white p-2 rounded-md shadow-md ${isRTL ? 'left-2' : 'right-2'}`}>
                                <a
                                    href={`https://www.openstreetmap.org/?mlat=${formData.address.latitude}&mlon=${formData.address.longitude}#map=16/${formData.address.latitude}/${formData.address.longitude}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`text-sm text-blue-600 flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 ${isRTL ? 'ml-1' : 'mr-1'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 9l3 3m0 0l-3 3m3-3H8m13 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    {t('register.location.adjustOnMap')}
                                </a>
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-center justify-center h-full bg-gray-100">
                            <p className={`text-gray-500 ${isRTL ? 'text-right' : 'text-left'}`}>
                                {t('register.location.mapPlaceholder')}
                            </p>
                        </div>
                    )}
                </div>

                {/* Instructions pour OpenStreetMap */}
                <div className={`mt-4 p-3 bg-blue-50 rounded-md text-sm text-blue-800 ${isRTL ? 'text-right' : 'text-left'}`}>
                    <h4 className="font-medium mb-1">{t('register.location.howToAdjust')}</h4>
                    <ol className={`${isRTL ? 'pr-5' : 'pl-5'} space-y-1 ${isRTL ? 'list-decimal' : 'list-decimal'}`} style={{listStyle: isRTL ? 'arabic-indic' : 'decimal'}}>
                        <li>{t('register.location.instructions.step1')}</li>
                        <li>{t('register.location.instructions.step2')}</li>
                        <li>{t('register.location.instructions.step3')}</li>
                        <li>{t('register.location.instructions.step4')}</li>
                    </ol>
                </div>
            </div>
        </div>
    );

    // Section des horaires d'ouverture
    const renderBusinessHours = () => (
        <div className={sectionClassName}>
            <div className={`flex justify-between items-center mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <h3 className={`text-lg font-medium text-gray-900 ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('register.businessHours.title')}
                </h3>
                <button
                    type="button"
                    onClick={() => setShowSchedule(!showSchedule)}
                    className={`text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}
                >
                    {showSchedule ? (
                        <>
                            {t('register.businessHours.hide')}
                            <ChevronUp className={`h-4 w-4 ${isRTL ? 'mr-1' : 'ml-1'}`} />
                        </>
                    ) : (
                        <>
                            {t('register.businessHours.show')}
                            <ChevronDown className={`h-4 w-4 ${isRTL ? 'mr-1' : 'ml-1'}`} />
                        </>
                    )}
                </button>
            </div>

            {showSchedule && (
                <div className="space-y-4">
                    {getWeekdays().map(day => (
                        <div key={day.id} className="p-4 border border-gray-200 rounded-lg">
                            <div className={`flex items-center justify-between mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                <h4 className={`font-medium ${isRTL ? 'text-right' : 'text-left'}`}>{day.label}</h4>
                                <label className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                                    <input
                                        type="checkbox"
                                        className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 mr-2"
                                        checked={getBusinessHour(day.id).closed}
                                        onChange={e => handleBusinessHoursChange(day.id, 'closed', e.target.checked)}
                                    />
                                    <span className="text-sm text-gray-700">{t('register.businessHours.closed')}</span>
                                </label>
                            </div>

                            {!getBusinessHour(day.id).closed && (
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className={labelClassName}>{t('register.businessHours.openingTime')}</label>
                                        <input
                                            type="time"
                                            required={!getBusinessHour(day.id).closed}
                                            className={getInputClassName(`businessHours.${day.id}.open`)}
                                            value={getBusinessHour(day.id).open}
                                            onChange={e => handleBusinessHoursChange(day.id, 'open', e.target.value)}
                                            disabled={getBusinessHour(day.id).closed}
                                            dir="ltr"
                                        />
                                        <FieldError name={`businessHours.${day.id}.open`} />
                                    </div>
                                    <div>
                                        <label className={labelClassName}>{t('register.businessHours.closingTime')}</label>
                                        <input
                                            type="time"
                                            required={!getBusinessHour(day.id).closed}
                                            className={getInputClassName(`businessHours.${day.id}.close`)}
                                            value={getBusinessHour(day.id).close}
                                            onChange={e => handleBusinessHoursChange(day.id, 'close', e.target.value)}
                                            disabled={getBusinessHour(day.id).closed}
                                            dir="ltr"
                                        />
                                        <FieldError name={`businessHours.${day.id}.close`} />
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    // Section des spécialités et types de véhicules
    const renderSpecialitiesAndVehicles = () => (
        <div className="space-y-8" dir={isRTL ? 'rtl' : 'ltr'}>
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                {/* Spécialités */}
                <div className={sectionClassName}>
                    <h3 className={`text-lg font-medium text-gray-900 mb-4 ${isRTL ? 'text-right' : 'text-left'}`}>
                        {t('register.specialties.title')}
                    </h3>

                    {/* Option "Toutes les spécialités" */}
                    <div className="mb-4 pb-2 border-b border-gray-100">
                        <label className={`flex items-center font-medium text-blue-700 ${isRTL ? 'justify-start' : 'justify-start'}`}>
                            <input
                                type="checkbox"
                                className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                checked={formData.specialities.length === getSpecialityOptions().length}
                                onChange={e => handleAllSpecialities(e.target.checked)}
                            />
                            <span className={`${isRTL ? 'text-right mr-2' : 'text-left ml-2'}`}>
                  {t('register.specialties.allSpecialties')}
                </span>
                        </label>
                    </div>

                    <div className="space-y-3">
                        {getSpecialityOptions().map(option => (
                            <label key={option.value} className={`flex items-center ${isRTL ? 'justify-start' : 'justify-start'}`}>
                                <input
                                    type="checkbox"
                                    className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    checked={formData.specialities.includes(option.value)}
                                    onChange={(e) => {
                                        const specialities = e.target.checked
                                            ? [...formData.specialities, option.value]
                                            : formData.specialities.filter(s => s !== option.value);
                                        setFormData({ ...formData, specialities });
                                    }}
                                />
                                <span className={`${isRTL ? 'text-right mr-2' : 'text-left ml-2'} text-sm text-gray-700`}>
                      {option.label}
                    </span>
                            </label>
                        ))}
                    </div>

                    {fieldErrors.specialities && (
                        <p className={`text-red-500 text-sm mt-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                            {fieldErrors.specialities}
                        </p>
                    )}
                </div>

                {/* Types de véhicules */}
                <div className={sectionClassName}>
                    <h3 className={`text-lg font-medium text-gray-900 mb-4 ${isRTL ? 'text-right' : 'text-left'}`}>
                        {t('register.vehicleTypes.title')}
                    </h3>

                    {/* Option "Tous les types de véhicules" */}
                    <div className="mb-4 pb-2 border-b border-gray-100">
                        <label className={`flex items-center font-medium text-blue-700 ${isRTL ? 'justify-start' : 'justify-start'}`}>
                            <input
                                type="checkbox"
                                className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                checked={formData.vehicleTypes.length === getVehicleTypeOptions().length}
                                onChange={e => handleAllVehicleTypes(e.target.checked)}
                            />
                            <span className={`${isRTL ? 'text-right mr-2' : 'text-left ml-2'}`}>
                  {t('register.vehicleTypes.allTypes')}
                </span>
                        </label>
                    </div>

                    <div className="space-y-3">
                        {getVehicleTypeOptions().map(option => (
                            <label key={option.value} className={`flex items-center ${isRTL ? 'justify-start' : 'justify-start'}`}>
                                <input
                                    type="checkbox"
                                    className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    checked={formData.vehicleTypes.includes(option.value)}
                                    onChange={(e) => {
                                        const vehicleTypes = e.target.checked
                                            ? [...formData.vehicleTypes, option.value]
                                            : formData.vehicleTypes.filter(v => v !== option.value);
                                        setFormData({ ...formData, vehicleTypes });
                                    }}
                                />
                                <span className={`${isRTL ? 'text-right mr-2' : 'text-left ml-2'} text-sm text-gray-700`}>
                      {option.label}
                    </span>
                            </label>
                        ))}
                    </div>

                    {fieldErrors.vehicleTypes && (
                        <p className={`text-red-500 text-sm mt-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                            {fieldErrors.vehicleTypes}
                        </p>
                    )}
                </div>
            </div>

            {/* Conditions Générales d'Utilisation */}
            {renderTermsAndConditions()}
        </div>
    );

    // Rendu principal du composant
    return (
        <div className={`min-h-screen bg-gradient-to-b from-blue-50 to-white py-12 px-4 sm:px-6 lg:px-8 ${isRTL ? 'rtl' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
            <div className="max-w-4xl mx-auto">
                <div className="bg-white rounded-xl shadow-xl px-8 py-8">
                    {/* Header */}
                    <div className="flex flex-col items-center mb-8">
                        <div className={`w-full flex ${isRTL ? 'justify-end' : 'justify-start'} mb-4`}>
                            <Link
                                to="/"
                                className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-bold text-[#7F5FFA] bg-[#E4EDFF] hover:bg-[#D6FFEA] hover:text-[#00493E] rounded-full transition-all ${isRTL ? 'flex-row-reverse' : ''}`}
                            >
                                <Home className="w-4 h-4" />
                                <span>{t('register.backToHome')}</span>
                            </Link>
                        </div>
                        <img src={icon} alt="Inspecly Logo" width={80} height={80} />
                        <h2 className={`mt-4 text-3xl font-bold text-gray-900 text-center`}>
                            {t('register.title')}
                        </h2>
                        <p className={`mt-2 text-center text-sm text-gray-600`}>
                            {t('register.subtitle')}
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-8">
                        {/* Erreur générale */}
                        {error && (
                            <div className="rounded-md bg-red-50 p-4 text-sm text-red-700 border border-red-200">
                                <div className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                                    <svg className={`h-5 w-5 text-red-400 ${isRTL ? 'ml-2' : 'mr-2'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span className={isRTL ? 'text-right' : 'text-left'}>{error}</span>
                                </div>
                            </div>
                        )}

                        {/* Section Offre Gratuite */}
                        {renderFreeOfferSection()}

                        {/* Indicateur de progression */}
                        <ProgressIndicator />

                        {/* Afficher l'étape actuelle */}
                        {currentStep === 1 && renderPersonalInfo()}
                        {currentStep === 2 && renderAddressInfo()}
                        {currentStep === 3 && renderBusinessHours()}
                        {currentStep === 4 && renderSpecialitiesAndVehicles()}

                        {/* Navigation des étapes */}
                        <div className={`flex justify-between mt-8 ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <button
                                type="button"
                                onClick={prevStep}
                                className="py-2 px-4 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200"
                            >
                                {currentStep > 1 ? t('register.navigation.previous') : t('register.navigation.back')}
                            </button>

                            {currentStep < 4 ? (
                                <button
                                    type="button"
                                    onClick={nextStep}
                                    className="py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                                >
                                    {t('register.navigation.next')}
                                </button>
                            ) : (
                                <button
                                    type="submit"
                                    disabled={
                                        loading ||
                                        formData.specialities.length === 0 ||
                                        formData.vehicleTypes.length === 0 ||
                                        !acceptedTerms
                                    }
                                    className={`flex justify-center py-2 px-6 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 ${
                                        (loading || formData.specialities.length === 0 || formData.vehicleTypes.length === 0 || !acceptedTerms) ? 'opacity-75 cursor-not-allowed' : ''
                                    }`}
                                >
                                    {loading ? (
                                        <span className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <svg className={`animate-spin h-5 w-5 text-white ${isRTL ? 'ml-3' : '-ml-1 mr-3'}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                                            {t('register.navigation.processing')}
                      </span>
                                    ) : (
                                        t('register.navigation.completeRegistration')
                                    )}
                                </button>
                            )}
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};